/**
 * OutsourcingMarketplacePanel (US-17-GR-24)
 *
 * Displays outsourcing marketplace health metrics filtered by the chosen
 * time window, including the PRD adoption target line (20%). Data comes from
 * adminMetricsService — no mocked data.
 *
 * Recovered from the deleted user-app panel (frontend commit 9c418bd3f^
 * src/components/agent/admin/OutsourcingMarketplacePanel.tsx) and re-homed into
 * ChellessAdmin conventions.
 *
 * Metric names are aligned to OutsourcingMetricsResponse (AdminMetricsResponse.cs):
 *   totalActiveRequests, totalBidsSubmitted, averageBidsPerRequest,
 *   bidAcceptanceRate, medianTimeToFirstBidHours, providerRatingDistribution,
 *   topServiceCategories, outsourcingAdoptionRate, adoptionTarget.
 */

import { useEffect, useState, useCallback, startTransition } from 'react';
import { Briefcase, RefreshCw, AlertCircle } from 'lucide-react';
import type { OutsourcingMetrics, MetricWindow } from '../services/adminMetricsService';
import { getOutsourcingMetrics } from '../services/adminMetricsService';
import { useLatestRequest } from '../hooks/useLatestRequest';

interface OutsourcingMarketplacePanelProps {
  window: MetricWindow;
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function decimal(n: number, places = 1): string {
  return n.toFixed(places);
}

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-zinc-800/50 rounded-lg p-4">
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-xs text-zinc-400 mt-1">{label}</p>
      {sub && <p className="text-xs text-zinc-600 mt-0.5">{sub}</p>}
    </div>
  );
}

export function OutsourcingMarketplacePanel({ window }: OutsourcingMarketplacePanelProps) {
  const [metrics, setMetrics] = useState<OutsourcingMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const beginLoad = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginLoad();
    setLoading(true);
    setError(null);
    try {
      const data = await getOutsourcingMetrics(window);
      if (!isCurrent()) return;
      setMetrics(data);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof Error ? err.message : 'Failed to load outsourcing data');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [window, beginLoad]);

  useEffect(() => {
    startTransition(() => { void load(); });
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm text-red-400 font-medium">Failed to load outsourcing metrics</p>
          <p className="text-xs text-zinc-500 mt-0.5">{error}</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metrics) return null;

  // Work item 17426: the API used to catch a ServiceManager S2S failure and answer 200 with zeros,
  // so "the marketplace is idle" and "the credential is broken" rendered identically — and zero
  // adoption is a number an operator acts on. It now says so, and the panel refuses to draw the
  // figures rather than showing zeros nobody measured.
  if (metrics.dataUnavailable) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-medium text-zinc-200">Outsourcing &amp; Marketplace</h3>
        </div>
        <div
          role="status"
          className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg"
        >
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-amber-300 font-medium">
              Outsourcing figures unavailable — not zero
            </p>
            <p className="text-xs text-zinc-400 mt-0.5">
              {metrics.dataUnavailableReason ??
                'The outsourcing metrics could not be read from ServiceManager. These figures are unknown, not zero.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-zinc-800 text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const adoptionPct = (metrics.outsourcingAdoptionRate * 100).toFixed(1);
  const adoptionTargetPct = (metrics.adoptionTarget * 100).toFixed(1);
  const onTarget = metrics.outsourcingAdoptionRate >= metrics.adoptionTarget;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-medium text-zinc-200">Outsourcing &amp; Marketplace</h3>
      </div>

      {/* Request &amp; bid volume */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiCard
          label="Active Requests"
          value={metrics.totalActiveRequests.toLocaleString()}
        />
        <KpiCard
          label="Bids Submitted"
          value={metrics.totalBidsSubmitted.toLocaleString()}
        />
        <KpiCard
          label="Avg Bids / Request"
          value={decimal(metrics.averageBidsPerRequest)}
        />
      </div>

      {/* Acceptance &amp; timing */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <KpiCard
          label="Bid Acceptance Rate"
          value={pct(metrics.bidAcceptanceRate)}
        />
        <KpiCard
          label="Median Time to First Bid"
          value={`${decimal(metrics.medianTimeToFirstBidHours)} hrs`}
        />
        <KpiCard
          label="Avg Completion Days"
          value={decimal(metrics.averageOutsourcedTaskCompletionDays)}
        />
      </div>

      {/* Adoption target gauge (PRD target line 20%) */}
      <div className="bg-zinc-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-zinc-400">Outsourcing Adoption Rate</span>
          <span className={`text-xs font-medium ${onTarget ? 'text-emerald-400' : 'text-amber-400'}`}>
            {adoptionPct}% / target {adoptionTargetPct}%
          </span>
        </div>
        <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${onTarget ? 'bg-emerald-500' : 'bg-amber-400'}`}
            style={{ width: `${Math.min(metrics.outsourcingAdoptionRate / metrics.adoptionTarget * 100, 100).toFixed(1)}%` }}
            aria-label={`Adoption ${adoptionPct}% of ${adoptionTargetPct}% target`}
          />
        </div>
      </div>

      {/* Top service categories */}
      {metrics.topServiceCategories.length > 0 && (
        <div>
          <p className="text-xs text-zinc-400 mb-2">Top Service Categories</p>
          <div className="space-y-1.5">
            {metrics.topServiceCategories.slice(0, 5).map((cat) => (
              <div key={cat.name} className="flex items-center justify-between">
                <span className="text-xs text-zinc-300 truncate">{cat.name}</span>
                <span className="text-xs text-zinc-500 ml-2 flex-shrink-0">{cat.count} requests</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Provider rating distribution */}
      {metrics.providerRatingDistribution.some((v) => v > 0) && (
        <div>
          <p className="text-xs text-zinc-400 mb-2">Provider Ratings</p>
          <div className="flex items-end gap-1 h-10">
            {metrics.providerRatingDistribution.map((count, idx) => {
              const maxCount = Math.max(...metrics.providerRatingDistribution, 1);
              const barHeight = Math.round((count / maxCount) * 100);
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-0.5">
                  <div
                    className="w-full bg-violet-500/70 rounded-sm"
                    style={{ height: `${barHeight}%` }}
                    title={`${idx + 1} star: ${count}`}
                  />
                  <span className="text-[10px] text-zinc-600">{idx + 1}★</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default OutsourcingMarketplacePanel;
